import { AtSign, Check, Copy, Facebook, Linkedin, Mail, MessageCircle, MessagesSquare, Send, Share2, Twitter, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';

function copyText(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  return copied ? Promise.resolve() : Promise.reject(new Error('Impossibile copiare il link'));
}

export default function ShareButton({ title = 'Salirà!', text = 'Guarda i prezzi dei carburanti su Salirà!' }) {
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle');
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const url = typeof window === 'undefined' ? '' : window.location.href;
  const encodedUrl = encodeURIComponent(url);
  const encodedMessage = encodeURIComponent(`${text} ${url}`);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button')?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll('a[href], button:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [open]);

  const openFallback = () => {
    triggerRef.current = document.activeElement;
    setCopyStatus('idle');
    setOpen(true);
  };

  const handleShare = async () => {
    if (!navigator.share) {
      openFallback();
      return;
    }

    try {
      await navigator.share({ title, text, url });
    } catch (error) {
      if (error?.name !== 'AbortError') openFallback();
    }
  };

  const handleCopy = async () => {
    try {
      await copyText(url);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  };

  const shareOptions = [
    { label: 'WhatsApp', href: `https://wa.me/?text=${encodedMessage}`, icon: MessageCircle },
    { label: 'Telegram', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(text)}`, icon: Send },
    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, icon: Facebook },
    { label: 'X / Twitter', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodedUrl}`, icon: Twitter },
    { label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, icon: Linkedin },
    { label: 'Reddit', href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(title)}`, icon: MessagesSquare },
    { label: 'Bluesky', href: `https://bsky.app/intent/compose?text=${encodedMessage}`, icon: AtSign },
    { label: 'Email', href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodedMessage}`, icon: Mail },
  ];

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleShare}
        aria-label="Condividi questa lettura"
        className="shrink-0 bg-md-surface-container text-md-on-surface hover:bg-md-primary-container"
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
      </Button>

      {open && createPortal(
        <div className="fixed inset-0 z-40 bg-md-on-surface/45" onMouseDown={() => setOpen(false)}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-dialog-title"
          aria-describedby="share-dialog-description"
          onMouseDown={event => event.stopPropagation()}
          className="fixed inset-x-3 bottom-3 z-50 max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-[32px_32px_14px_32px] bg-md-surface p-5 text-md-on-surface shadow-md3 focus:outline-none sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:p-7"
        >
          <div className="flex items-start justify-between gap-5">
            <div>
              <h2 id="share-dialog-title" className="text-xl font-bold tracking-[-0.02em]">Condividi questa lettura</h2>
              <p id="share-dialog-description" className="mt-1 text-sm leading-6 text-md-on-surface-variant">
                Invia il riepilogo oppure copia il collegamento alla pagina.
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Chiudi" className="-mr-2 -mt-2 shrink-0 text-md-on-surface-variant hover:bg-md-surface-container">
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {shareOptions.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target={label === 'Email' ? undefined : '_blank'}
                rel={label === 'Email' ? undefined : 'noopener noreferrer'}
                className="md-press flex min-h-[84px] flex-col items-center justify-center gap-2 rounded-[22px] bg-md-secondary-container px-2 py-3 text-center text-xs font-bold text-md-on-secondary-container hover:bg-md-surface-container-high focus-visible:outline-none"
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                {label}
              </a>
            ))}
          </div>

          <div className="mt-4 rounded-[24px_24px_9px_24px] bg-md-surface-container p-2">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate pl-2 text-xs text-md-on-surface-variant">{url}</span>
              <Button type="button" onClick={handleCopy} className="shrink-0 bg-md-primary text-md-on-primary hover:bg-md-primary/90">
                {copyStatus === 'copied' ? <Check className="mr-2 h-4 w-4" aria-hidden="true" /> : <Copy className="mr-2 h-4 w-4" aria-hidden="true" />}
                {copyStatus === 'copied' ? 'Copiato' : 'Copia link'}
              </Button>
            </div>
          </div>
          <p className="mt-2 min-h-5 text-xs text-md-on-surface-variant" aria-live="polite">
            {copyStatus === 'copied' && 'Link copiato negli appunti.'}
            {copyStatus === 'error' && 'Copia non riuscita: seleziona il link qui sopra.'}
          </p>
        </div>
        </div>,
        document.body
      )}
    </>
  );
}
